export const useUserMenusStore = defineStore('userMenus', () => {
  // Read from the Nuxt app instead of Vuetify's useDisplay(): that one needs a
  // component instance, and this store is first instantiated from plugins/6.init.ts,
  // outside any component.
  // Caveat: useNuxtApp() throws outside a plugin, Nuxt hook, middleware or Vue setup.
  // Safe only while this store is first instantiated inside one of those contexts —
  // move that to an async callback, timer or event handler and it breaks at boot.
  const { $vuetify } = useNuxtApp()
  const display = ref($vuetify.display)
  const userStore = useUserStore()
  const {
    showCampaignsMergedUI,
    canUseVoiceCalls,
    hasCalendarAccess,
    hasAccessToLegacyIntegrations,
    hasSharedTemplatesAccess,
    hasSeatManagementAccess,
    hasHCMIntegrationsAccess
  } = useFeatureFlags()
  const { onlyLettersAndNumbers } = useFilters()
  const { CAMPAINGS_PERMISSIONS } = useActivePhoneNumber()

  // STATE
  const phoneMenus = ref<Array<any>>([])
  const phoneMenusLoaded = ref(false)

  // PHONE ROUTES: one set per phone number, built when the services land.
  const phonePath = (phone: any) => `${phone.tenantId}/${phone.id}`
  const isWhatsappOnly = (phone: any) => !phone.enable_sms && phone.enable_whatsapp
  const canViewCampaigns = (phone: any) => phone.permissions?.includes(CAMPAINGS_PERMISSIONS.VIEW)
  const hasAnyCampaigns = (phone: any) => phone.campaigns_enabled || phone.drip_campaigns_enabled
  const getInboxRoute = (phone: any) => {
    if (!phone.chat_enabled) { return null }
    return {
      to: `/inbox/${phonePath(phone)}`,
      title: 'Inbox',
      icon: 'mdi-inbox',
      description: 'Start a conversation by accessing the Inbox interface!'
    }
  }
  const getContactsRoute = (phone: any) => ({
    to: `/contacts/${phonePath(phone)}`,
    title: 'Contacts',
    icon: 'mdi-contacts',
    description: 'Access, search and create new contacts.'
  })
  const getWhatsappTemplatesRoute = (phone: any) => {
    if (!isWhatsappOnly(phone)) { return null }
    return {
      to: `/whatsapp-templates/${phonePath(phone)}`,
      title: 'WhatsApp Templates',
      icon: 'mdi-whatsapp',
      description: 'Create and modify Whatsapp templates.'
    }
  }

  const getCampaignRoutes = (phone: any) => {
    if (!canViewCampaigns(phone)) { return [] }
    const oneOffPath = `/oneoff-campaigns/${phonePath(phone)}`
    const dripPath = `/drip-campaigns/${phonePath(phone)}`
    const campaigns = {
      title: 'Campaigns',
      description: 'Create and modify existing campaigns.',
      icon: 'mdi-bullhorn'
    }
    if (showCampaignsMergedUI.value && hasAnyCampaigns(phone)) {
      return [{ ...campaigns, to: isWhatsappOnly(phone) ? oneOffPath : dripPath }]
    }
    const routes = []
    if (phone.campaigns_enabled) {
      routes.push({ ...campaigns, to: oneOffPath })
    }
    if (!isWhatsappOnly(phone) && hasAnyCampaigns(phone)) {
      routes.push({
        to: phone.drip_campaigns_enabled ? dripPath : '/early-access/drip-campaigns/',
        title: 'Drip Campaigns',
        description: 'Create and modify existing drip campaigns.',
        icon: 'mdi-multicast'
      })
    }
    return routes
  }

  const getCallLogsRoute = (phone: any) => {
    if (phone.read_only || !canUseVoiceCalls.value) { return null }
    return {
      to: `/call-logs/${phonePath(phone)}`,
      title: 'Call Logs',
      icon: 'mdi-phone-log',
      description: 'View the call history for this phone number.'
    }
  }

  const buildPhoneRoutes = (phone: any) => [
    getInboxRoute(phone),
    getContactsRoute(phone),
    getWhatsappTemplatesRoute(phone),
    ...getCampaignRoutes(phone),
    getCallLogsRoute(phone)
  ].filter(Boolean)

  const sortByInboxName = (inbox: any, otherInbox: any) => {
    if (!inbox.inbox_name && !otherInbox.inbox_name) {
      return onlyLettersAndNumbers(inbox.phone_number) < onlyLettersAndNumbers(otherInbox.phone_number) ? -1 : 1
    }
    if (!inbox.inbox_name) { return 1 }
    if (!otherInbox.inbox_name) { return -1 }
    return inbox.inbox_name.localeCompare(otherInbox.inbox_name)
  }

  // GLOBAL ROUTES: one set per tenant, derived rather than stored.
  const getCalendarRoute = () => ({
    to: hasCalendarAccess.value ? '/calendar' : '/early-access/calendar',
    title: 'Calendar Integration',
    icon: 'mdi-calendar-sync',
    description: 'The calendar integration allows you to configure your calendar from Emissary.',
    showInHome: true
  })
  const getWhatsappRoute = () => ({
    to: '/early-access/whatsapp-messaging',
    title: 'WhatsApp',
    icon: 'mdi-whatsapp',
    description: 'Connect with candidates and employees around the world with WhatsApp Business, fully integrated with Emissary.',
    showInHome: true
  })
  const getMetricsRoute = (tenantId: string) => ({
    to: `/metrics/messages-campaigns/${tenantId}/`,
    title: 'Metrics',
    icon: 'mdi-chart-bar',
    description: 'Here you can see interactions between seats and their contacts.',
    showInHome: true
  })
  const getIntegrationsRoute = (tenantId: string) => ({
    to: hasHCMIntegrationsAccess.value ? `/integrations/${tenantId}` : `/integrations/${tenantId}/legacy`,
    title: 'Integrations',
    icon: 'mdi-cogs',
    description: 'The integrations page helps you to manage your integrations with external systems.',
    showInHome: true
  })
  const getSeatsRoute = (tenantId: string) => ({
    to: `/seats/${tenantId}`,
    title: 'Manage Users',
    icon: 'mdi-account-group',
    description: 'Here you will be able to manage your users and their permissions.',
    showInHome: true
  })
  const getSharedTemplatesRoute = (tenantId: string) => ({
    to: `/shared-templates/${tenantId}`,
    title: 'Shared Templates',
    icon: 'mdi-note-text-outline',
    description: 'Here you will be able to manage shared templates.',
    showInHome: true
  })

  // GETTERS
  const seatPhoneNumberId = computed(() => {
    return phoneMenus.value.find(phone => phone.is_seat)?.id || null
  })
  const hasWhatsappPhone = computed(() => {
    return phoneMenus.value.some(phoneMenu => phoneMenu.enable_whatsapp)
  })
  const globalRoutes = computed(() => {
    const tenantId = userStore.currentTenantInfo?.id
    if (!tenantId) { return [] }
    return [
      getCalendarRoute(),
      !hasWhatsappPhone.value ? getWhatsappRoute() : null,
      display.value.mdAndUp ? getMetricsRoute(tenantId) : null,
      hasAccessToLegacyIntegrations.value || hasHCMIntegrationsAccess.value ? getIntegrationsRoute(tenantId) : null,
      hasSeatManagementAccess.value ? getSeatsRoute(tenantId) : null,
      hasSharedTemplatesAccess.value ? getSharedTemplatesRoute(tenantId) : null
    ].filter(Boolean)
  })

  // ACTIONS
  const activePhoneMenuFromPath = (path: any) => {
    return phoneMenus.value.find(phoneMenu => phoneMenu.routes.some((route: any) => path === route.to))
  }
  const phoneMenuById = (id: any) => {
    return phoneMenus.value.find(phoneMenu => phoneMenu.id?.toString() === id?.toString())
  }
  const setPhoneMenus = (phones: Array<any> | null) => {
    phoneMenus.value = phones
      ?.map((phone: any) => ({ ...phone, routes: buildPhoneRoutes(phone) }))
      .sort(sortByInboxName) || []
    phoneMenusLoaded.value = true
  }
  const $reset = () => {
    phoneMenus.value = []
    phoneMenusLoaded.value = false
  }

  return {
    // STATE
    phoneMenus,
    phoneMenusLoaded,
    // GETTERS
    seatPhoneNumberId,
    globalRoutes,
    // ACTIONS
    phoneMenuById,
    activePhoneMenuFromPath,
    setPhoneMenus,
    $reset
  }
})
